// GifJsRecorder.jsx
import { useRef, useState, useEffect } from 'react';
import GIF from 'gif.js.optimized';
import Modal from 'react-modal';
import { MdDownloading } from 'react-icons/md';
import { PiGifFill } from "react-icons/pi";
import { useIframeCapture } from './useIframeCapture';
import { triggerDownload } from './triggerDownload';

const RESOLUTIONS = {
  S: { label: 'Small',  scale: 0.3  },
  M: { label: 'Medium', scale: 0.5 },
  L: { label: 'Large',  scale: 1.0  },
};

const QUALITIES = {
  low:  { label: 'Low',    quality: 10, dither: false },
  med:  { label: 'Medium', quality: 5,  dither: false },
  high: { label: 'High',   quality: 1,  dither: 'FloydSteinberg' },
};

Modal.setAppElement('#root');

const customGifStyles = {
  overlay: {
    position: 'fixed',
    inset: '0px',
    backgroundColor: '#000000B3',
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    border: '1px solid #00ff00',
    background: '#000',
    borderRadius: '0px',
    transform: 'translate(-50%, -50%)',
  },
};

export default function GifJsRecorder({ iframeRef, durationMs = 5000, fps = 30, fileName="cypherdude"}) {
  const { start, stop } = useIframeCapture(iframeRef);
  const [status, setStatus]         = useState('idle'); // idle | settings | picking | recording | encoding | done
  const [progress, setProgress]     = useState(0);
  const [resolution, setResolution] = useState('M');
  const [quality, setQuality]       = useState('med');
  const rafRef = useRef(0);

  // The modal is open whenever we're doing anything other than idle/done.
  const modalIsOpen = status === 'settings' || status === 'encoding';

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    stop();
  }, [stop]);

  const openSettings = () => {
    setProgress(0);
    setStatus('settings');
  };

  const closeModal = () => {
    // Only allow closing from the settings phase — while recording or encoding,
    // react-modal's onRequestClose is a no-op.
    if (status === 'settings') {
      stop();
      setStatus('idle');
    }
  };

  const startRecording = async () => {
    try {
      setStatus('picking');
      setProgress(0);

      const { scale }               = RESOLUTIONS[resolution];
      const { quality: q, dither }  = QUALITIES[quality];

      const { ctx, sw, sh } = await start({ scale });

      const gif = new GIF({
        workers: 4,
        quality: q,
        width: sw,
        height: sh,
        workerScript: '/gif.worker.js',
        dither,
      });

      gif.on('progress', p => setProgress(p));
      gif.on('finished', blob => {
        triggerDownload(blob, `${fileName}.gif`);
        setStatus('done');
        setTimeout(() => setStatus('idle'), 1500);
      });

      const frameDelay = 1000 / fps;
      const startedAt  = performance.now();
      let nextTick     = startedAt;
      setStatus('recording');

      const grab = now => {
        if (now - startedAt >= durationMs) {
          stop();                // kills Chrome's sharing indicator immediately
          setStatus('encoding'); // swap modal content to the "please wait" view
          gif.render();          // workers take over
          return;
        }
        if (now >= nextTick) {
          gif.addFrame(ctx, { copy: true, delay: frameDelay });
          nextTick += frameDelay;
        }
        rafRef.current = requestAnimationFrame(grab);
      };
      rafRef.current = requestAnimationFrame(grab);
    } catch (err) {
      console.error(err);
      stop();
      setStatus('idle');
    }
  };

  const isIdle = status === 'idle' || status === 'done';

  return (
    <>
      <button
        onClick={openSettings}
        disabled={!isIdle}
        className="chartBtn"
      >
        {isIdle ? <PiGifFill /> : <MdDownloading />}
      </button>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={status === 'settings'}
        shouldCloseOnEsc={status === 'settings'}
        style={customGifStyles}
        contentLabel="GIF recorder"
      >
        {status === 'settings' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>Resolution</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {Object.entries(RESOLUTIONS).map(([key, { label }]) => (
                  <button
                    key={key}
                    className={`uxBtn${resolution === key ? ' uxBtnActive' : ''}`}
                    onClick={() => setResolution(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>Quality</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {Object.entries(QUALITIES).map(([key, { label }]) => (
                  <button
                    key={key}
                    className={`uxBtn${quality === key ? ' uxBtnActive' : ''}`}
                    onClick={() => setQuality(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="uxBtn" onClick={closeModal}>Cancel</button>
              <button className="uxBtn" onClick={startRecording}>Start recording</button>
            </div>
          </>
        )}

        {status === 'recording' && (
          <p className="modalInfo">Capturing {durationMs / 1000}s of the token.</p>
        )}

        {status === 'encoding' && (
          <>
            <p className="modalInfo">
              Screen sharing has stopped. Please wait while the GIF is being built.
            </p>
            <ProgressBar value={progress} />
            <div style={{paddingRight:'10px', paddingLeft:'10px', marginTop: 8, fontVariantNumeric: 'tabular-nums', color: '#00ff00' }}>
              {Math.round(progress * 100)}%
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

function ProgressBar({ value }) {
  return (
    <div style={{ width: '95%', height: 8, background: '#111', border: '1px solid #00ff00', overflow: 'hidden' }}>
      <div
        style={{
          width: `${Math.round(value * 100)}%`,
          height: '100%',
          background: '#00ff00',
          transition: 'width 120ms linear',
        }}
      />
    </div>
  );
}